import { useTranslation } from 'react-i18next';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { cn, tailwindUtils } from '@/shared/ui/utils/tailwind';
import { UnstyledButton } from '@/shared/ui';
import type { Launch } from '../../types';
import { useEditLaunchForm } from './useEditLaunchForm';
import { EditLaunchFields } from './EditLaunchFields';
import { EditLaunchUnitsSection } from './EditLaunchUnitsSection';

export interface EditLaunchDrawerProps {
  agencyId: string;
  launch: Launch | null;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
  /** Called after a unit is added/removed so the caller can refetch the launch and board. */
  onUnitsChanged: () => void;
}

/**
 * Edit drawer for launch metadata (name/address/hold TTL) plus unit
 * inventory management (link/remove units), mirroring CreateLaunchDrawer's
 * shell.
 */
export function EditLaunchDrawer({ agencyId, launch, open, onClose, onUpdated, onUnitsChanged }: EditLaunchDrawerProps) {
  const { t } = useTranslation('features/launches');
  const { form, isSubmitting, handleSubmit } = useEditLaunchForm({
    agencyId,
    launch,
    onSuccess: () => {
      onUpdated();
      onClose();
    },
  });

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={t('edit.title')}
      density="compact"
      footer={
        <UnstyledButton
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          aria-label={t('edit.submit')}
          data-testid="edit-launch-submit"
          className={cn(
            tailwindUtils.buttonBase,
            'h-10 min-w-[7.5rem] px-(--space-5)',
            'bg-(--button-primary-bg) text-(--button-primary-text)',
            'hover:bg-(--button-primary-hover-bg) hover:shadow-[var(--shadow-md)]',
            'text-[length:var(--text-body)]',
            'font-[weight:var(--font-semibold)]',
            'leading-[var(--leading-snug)]',
            'font-[family-name:var(--font-family)]',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
        >
          {isSubmitting ? t('create.saving') : t('edit.submit')}
        </UnstyledButton>
      }
    >
      <div className="flex flex-col gap-[var(--space-5)]">
        <EditLaunchFields form={form} />
        {launch && (
          <EditLaunchUnitsSection agencyId={agencyId} launch={launch} onUnitsChanged={onUnitsChanged} />
        )}
      </div>
    </AdaptiveFormShell>
  );
}
